/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useRef, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  IconButton,
  Button,
  Chip,
  Paper,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Badge,
  CircularProgress,
} from "@mui/material";
import {
  VideoLibrary as VideoIcon,
  PlayArrow as PlayIcon,
  Delete as DeleteIcon,
  OndemandVideo as DemoIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  CheckCircle as CheckIcon,
  Save as SaveIcon,
  RotateLeft as UndoIcon,
  FitnessCenter as FitnessCenterIcon,
  Description as DescriptionIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import moment from "moment";
import useSharedVideosByClient from "../../../../../hooks/useSharedVideosByClient";
import useEvents from "../../../../../hooks/useEvents";
import { useExercisesBigQuery } from "../../../../../hooks/useExercisesBigQuery";
import useVideoThumbnails from "../../../../../hooks/useVideoThumbnails";
import firebase from "firebase/compat/app";

// Shared styled components (consistent with other tabs)
const StyledVideosCard = styled(Card)(({ theme }) => ({
  height: "auto",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  transition: "box-shadow 0.3s ease",
  "&:hover": {
    boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
  },
}));

const VideosHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const VideosContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
  paddingTop: theme.spacing(2),
}));

const ActionButton = styled(Button)(() => ({
  borderRadius: 20,
  padding: "8px 20px",
  textTransform: "none",
  fontWeight: 600,
}));

const VideoCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "isProcessed",
})(({ theme, isProcessed }) => ({
  borderRadius: theme.spacing(1.5),
  overflow: "hidden",
  transition: "transform 0.2s ease, border 0.3s ease",
  "&:hover": {
    transform: "scale(1.03)",
  },
  cursor: "pointer",
  border: isProcessed
    ? `1px solid ${theme.palette.success.main}`
    : `1px solid ${theme.palette.warning.main}`,
}));

const VideoThumbnail = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isProcessed",
})(({ theme, isProcessed }) => ({
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: isProcessed
      ? "rgba(76, 175, 80, 0.1)" // Green tint for processed
      : "rgba(255, 152, 0, 0.1)", // Orange tint for unprocessed
  },
}));

const ThumbnailContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  height: 160,
  backgroundColor: "black",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
}));

const ThumbnailSpinner = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  zIndex: 3,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const ThumnailVideo = styled("video")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "center",
});

const StatusChip = styled(Chip)(({ theme }) => ({
  position: "absolute",
  top: 8,
  right: 8,
  zIndex: 2,
}));

const TypeChip = styled(Chip)(({ theme }) => ({
  position: "absolute",
  top: 8,
  left: 8,
  zIndex: 2,
  maxWidth: "calc(80% - 85px)", // Leave room for status chip
  "& .MuiChip-label": {
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}));

const PlayIconOverlay = styled(PlayIcon)({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  fontSize: 50,
  color: "white",
  opacity: 0.8,
  zIndex: 1,
});

const UploadZone = styled(Paper)(({ theme }) => ({
  border: `2px dashed ${theme.palette.primary.main}`,
  backgroundColor: theme.palette.primary.light + "0A",
  borderRadius: theme.spacing(1),
  padding: theme.spacing(4),
  textAlign: "center",
  cursor: "pointer",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: theme.palette.primary.light + "15",
    borderColor: theme.palette.primary.dark,
  },
}));

// Enhanced Video tag layout for cards
const VideoTagLayout = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  padding: theme.spacing(1),
  display: "flex",
  justifyContent: "space-between",
  zIndex: 2,
}));

// Enhanced Videos Tab Component
const EnhancedVideosTab = ({ client }) => {
  const [selectedVideo, setSelectedVideo] = React.useState(null);
  const [openViewer, setOpenViewer] = React.useState(false);
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const [newNote, setNewNote] = React.useState("");
  const [sortMode, setSortMode] = React.useState("unprocessed");
  const [refreshTrigger, setRefreshTrigger] = React.useState(0); // Add refresh trigger state
  const videoRefs = useRef({});
  const events = useEvents();
  const currentUser = firebase.auth().currentUser;

  // Fetch exercises from BigQuery for name resolution
  const { data: exercisesData } = useExercisesBigQuery({
    search: "",
    type: "all",
    limit: 1000,
    offset: 0,
    enabled: true,
  });

  const exercisesMap = React.useMemo(() => {
    const exercises = exercisesData?.data?.exercises || [];
    const map = {};
    exercises.forEach((exercise) => {
      map[exercise.id] = exercise;
    });
    return map;
  }, [exercisesData]);

  // Helper function to get exercise name
  const getExerciseName = React.useCallback(
    (exerciseId) => {
      if (!exerciseId) return "Unknown Exercise";
      const exercise = exercisesMap[exerciseId];
      return exercise ? exercise.name : exerciseId;
    },
    [exercisesMap],
  );

  // Use the shared videos hook with refresh trigger
  const { videos, deleteSharedVideo, setVideoProcessedState } =
    useSharedVideosByClient({
      uid: client?.uid,
    });

  // Use the shared video thumbnails hook with lazy loading
  const {
    thumbnails,
    loadingVideoIds,
    generateThumbnails,
    registerElement,
    resetAll,
    clearCacheForVideos,
  } = useVideoThumbnails(videos, {
    lazy: true,
    maxRetries: 2,
    retryDelay: 1000,
    loadTimeout: 10000,
    concurrency: 3,
    organizationId: client?.organizationId || null,
  });

  // Handle refresh button click
  const handleRefreshVideos = () => {
    // Log the refresh action
    events.logButtonClick({
      buttonName: "Refresh Videos",
      pageName: "Client Videos",
      tabName: client?.uid,
    });

    // Clear thumbnails and cache using shared hook utilities
    resetAll();
    clearCacheForVideos(videos.map((v) => v.id));

    // Force a re-fetch of videos from Firebase by updating a timestamp in Firestore
    const refreshVideoData = async () => {
      try {
        // Update a document in the user's organization to trigger Firestore onSnapshot listeners
        if (client?.organizationId) {
          const refreshMarkerRef = firebase
            .firestore()
            .collection("organizations")
            .doc(client.organizationId)
            .collection("refresh_markers")
            .doc("shared_videos");

          await refreshMarkerRef.set(
            {
              lastRefreshed: firebase.firestore.FieldValue.serverTimestamp(),
              refreshedBy: currentUser?.uid || "unknown",
              clientId: client.uid,
            },
            { merge: true },
          );

          console.log("Video refresh triggered for client:", client.uid);
        }
      } catch (error) {
        console.error("Error refreshing videos:", error);
      }
    };

    refreshVideoData();

    // Update refresh trigger to cause component re-render
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleVideoClick = (video) => {
    setSelectedVideo(video);
    setNewNote(video.notes || "");
    setOpenViewer(true);

    events.logButtonClick({
      buttonName: "View Shared Video",
      pageName: "Client Videos",
      tabName: client?.uid,
    });
  };

  const handleToggleProcessed = async (video) => {
    if (!video) return;

    try {
      await setVideoProcessedState(video.id, currentUser.uid, !video.processed);

      events.logButtonClick({
        buttonName: video.processed
          ? "Mark Video Unprocessed"
          : "Mark Video Processed",
        pageName: "Client Videos",
        tabName: client?.uid,
      });

      // Update the selected video state to reflect the change
      setSelectedVideo((prev) => ({
        ...prev,
        processed: !prev.processed,
      }));
    } catch (error) {
      console.error("Error toggling video processed state:", error);
    }
  };

  const handleSaveNote = async () => {
    if (!selectedVideo) return;

    try {
      const videoRef = firebase
        .firestore()
        .collection(`organizations/${client.organizationId}/sharedVideos`)
        .doc(selectedVideo.id);

      await videoRef.update({
        notes: newNote,
        updatedOn: new Date(),
      });

      events.logButtonClick({
        buttonName: "Save Video Notes",
        pageName: "Client Videos",
        tabName: client?.uid,
      });
    } catch (error) {
      console.error("Error saving note:", error);
    }
  };

  const handleDeleteVideo = async (video) => {
    if (window.confirm("Are you sure you want to delete this video?")) {
      try {
        await deleteSharedVideo(video.id);
        setOpenViewer(false);

        events.logButtonClick({
          buttonName: "Delete Video",
          pageName: "Client Videos",
          tabName: client?.uid,
        });
      } catch (error) {
        console.error("Error deleting video:", error);
      }
    }
  };

  // Calculate statistics
  const processedVideos = videos.filter((video) => video.processed);
  const unprocessedVideos = videos.filter((video) => !video.processed);
  const mostRecentVideo =
    videos.length > 0
      ? videos.reduce((latest, video) => {
          return !latest.createdOn ||
            video.createdOn?.toDate() > latest.createdOn?.toDate()
            ? video
            : latest;
        }, {})
      : null;

  // Sort videos based on current sort mode
  const sortedVideos = React.useMemo(() => {
    const sorted = [...videos];

    // First sort by processed/unprocessed status
    if (sortMode === "unprocessed") {
      sorted.sort((a, b) => {
        // Sort unprocessed first
        if (!a.processed && b.processed) return -1;
        if (a.processed && !b.processed) return 1;

        // Then by date (oldest first for unprocessed to prioritize older submissions)
        if (!a.processed && !b.processed) {
          return a.createdOn?.toDate() - b.createdOn?.toDate();
        }

        // For processed videos, newest first
        return b.createdOn?.toDate() - a.createdOn?.toDate();
      });
    } else if (sortMode === "newest") {
      sorted.sort((a, b) => b.createdOn?.toDate() - a.createdOn?.toDate());
    } else if (sortMode === "oldest") {
      sorted.sort((a, b) => a.createdOn?.toDate() - b.createdOn?.toDate());
    }

    return sorted;
  }, [videos, sortMode]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return moment(date).format("MMM D, YYYY h:mm A");
  };

  const getVideoTypeLabel = (video) => {
    if (video.metadata?.exercise) {
      return "Exercise Video";
    }
    return "General Video";
  };

  const getVideoTypeIcon = (video) => {
    if (video.metadata?.exercise) {
      return <FitnessCenterIcon fontSize="small" />;
    }
    return <VideoIcon fontSize="small" />;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* Video Statistics */}
        <Grid item xs={12} md={4}>
          <StyledVideosCard>
            <VideosHeader>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <VideoIcon color="primary" sx={{ fontSize: 28 }} />
                <Typography variant="h6" fontWeight="bold">
                  Video Library
                </Typography>
              </Stack>
              <IconButton
                size="small"
                onClick={handleRefreshVideos}
                disabled={loadingVideoIds.size > 0}
                title="Refresh videos"
              >
                <RefreshIcon />
              </IconButton>
            </VideosHeader>
            <VideosContent>
              <Stack spacing={3}>
                {/* Video Count - Updated to show pending videos count */}
                <Box>
                  <Typography
                    variant="h2"
                    fontWeight="bold"
                    color={
                      unprocessedVideos.length > 0
                        ? "warning.main"
                        : "primary.main"
                    }
                    align="center"
                  >
                    {unprocessedVideos.length}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                  >
                    {unprocessedVideos.length === 1 ? "Video" : "Videos"}{" "}
                    Pending Review
                  </Typography>
                  {videos.length > unprocessedVideos.length && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      align="center"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      {videos.length - unprocessedVideos.length} processed
                    </Typography>
                  )}
                </Box>

                <Divider />

                {videos.length > 0 ? (
                  <>
                    {/* Quick Stats */}
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ borderBottom: "none", pl: 0 }}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              <Badge color="warning" variant="dot">
                                <VideoIcon color="primary" fontSize="small" />
                              </Badge>
                              <Typography variant="body2">
                                Waiting Review
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            sx={{ borderBottom: "none", pr: 0 }}
                            align="right"
                          >
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color={
                                unprocessedVideos.length > 0
                                  ? "warning.main"
                                  : "text.primary"
                              }
                            >
                              {unprocessedVideos.length}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ borderBottom: "none", pl: 0 }}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              <CheckIcon color="success" fontSize="small" />
                              <Typography variant="body2">Processed</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            sx={{ borderBottom: "none", pr: 0 }}
                            align="right"
                          >
                            <Typography variant="body2" fontWeight="bold">
                              {processedVideos.length}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ borderBottom: "none", pl: 0 }}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              <ScheduleIcon color="primary" fontSize="small" />
                              <Typography variant="body2">
                                Most Recent
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell
                            sx={{ borderBottom: "none", pr: 0 }}
                            align="right"
                          >
                            <Typography variant="body2" fontWeight="bold">
                              {mostRecentVideo
                                ? moment(
                                    mostRecentVideo.createdOn.toDate(),
                                  ).fromNow()
                                : "N/A"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>

                    {/* Category Breakdown */}
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Video Types
                      </Typography>
                      <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between">
                          <Chip
                            icon={<FitnessCenterIcon />}
                            label="Exercise Videos"
                            color="primary"
                            size="small"
                            variant="outlined"
                          />
                          <Typography variant="body2" fontWeight="bold">
                            {videos.filter((v) => v.metadata?.exercise).length}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                          <Chip
                            icon={<VideoIcon />}
                            label="General Videos"
                            color="secondary"
                            size="small"
                            variant="outlined"
                          />
                          <Typography variant="body2" fontWeight="bold">
                            {videos.filter((v) => !v.metadata?.exercise).length}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>
                  </>
                ) : (
                  <Paper
                    sx={{
                      p: 3,
                      textAlign: "center",
                      bgcolor: "grey.50",
                      borderStyle: "dashed",
                      borderWidth: 2,
                      borderColor: "grey.300",
                    }}
                  >
                    <VideoIcon
                      sx={{ fontSize: 48, color: "grey.400", mb: 2 }}
                    />
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      No shared videos yet
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Videos shared by the client will appear here
                    </Typography>
                  </Paper>
                )}
              </Stack>
            </VideosContent>
          </StyledVideosCard>
        </Grid>

        {/* Video Gallery */}
        <Grid item xs={12} md={8}>
          <StyledVideosCard>
            <VideosHeader>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <DemoIcon color="primary" sx={{ fontSize: 28 }} />
                <Typography variant="h6" fontWeight="bold">
                  Shared Videos
                </Typography>
                {unprocessedVideos.length > 0 && (
                  <Chip
                    label={`${unprocessedVideos.length} pending review`}
                    color="warning"
                    size="small"
                  />
                )}
                {loadingVideoIds.size > 0 && (
                  <Chip
                    icon={
                      <CircularProgress
                        size={16}
                        sx={{ color: "white !important" }}
                      />
                    }
                    label={`Loading ${loadingVideoIds.size} thumbnail${loadingVideoIds.size !== 1 ? "s" : ""}`}
                    color="info"
                    size="small"
                    sx={{ ml: 1 }}
                  />
                )}
              </Stack>
              <Stack direction="row" spacing={1}>
                <ActionButton
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    // Cycle through sort modes
                    if (sortMode === "unprocessed") setSortMode("newest");
                    else if (sortMode === "newest") setSortMode("oldest");
                    else setSortMode("unprocessed");
                  }}
                  startIcon={<ScheduleIcon />}
                >
                  {sortMode === "unprocessed"
                    ? "Priority Queue"
                    : sortMode === "newest"
                      ? "Newest First"
                      : "Oldest First"}
                </ActionButton>

                <ActionButton
                  size="small"
                  color="primary"
                  disabled={loadingVideoIds.size > 0}
                  onClick={generateThumbnails}
                  title="Refresh thumbnails"
                >
                  <RefreshIcon fontSize="small" />
                </ActionButton>
              </Stack>
            </VideosHeader>
            <VideosContent>
              {videos.length > 0 ? (
                <Grid container spacing={2}>
                  {sortedVideos.map((video) => (
                        <Grid item xs={12} sm={6} md={6} lg={4} key={video.id}>
                          <VideoCard
                            onClick={() => handleVideoClick(video)}
                            isProcessed={video.processed}
                          >
                            <VideoThumbnail isProcessed={video.processed}>
                              {/* Video thumbnail with properly positioned tags */}
                              <ThumbnailContainer
                                ref={(el) => {
                                  if (el) {
                                    registerElement(el, video.id);
                                  }
                                }}
                              >
                                {/* Display generated thumbnail if available */}
                                {thumbnails[video.id] ? (
                                  <Box
                                    component="img"
                                    src={thumbnails[video.id]}
                                    alt="Video thumbnail"
                                    sx={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                    }}
                                  />
                                ) : (
                                  <Box
                                    sx={{
                                      width: "100%",
                                      height: "100%",
                                      backgroundColor: "black",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  />
                                )}

                                {/* Loading spinner */}
                                {loadingVideoIds.has(video.id) && (
                                  <ThumbnailSpinner>
                                    <CircularProgress
                                      size={32}
                                      sx={{ color: "white" }}
                                    />
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "white",
                                        textAlign: "center",
                                        fontSize: "0.7rem",
                                      }}
                                    >
                                      Loading...
                                    </Typography>
                                  </ThumbnailSpinner>
                                )}

                                {/* Play button overlay - hide when loading */}
                                {!loadingVideoIds.has(video.id) && (
                                  <PlayIconOverlay />
                                )}

                                {/* Status indicator - fixed positioning */}
                                <StatusChip
                                  icon={
                                    video.processed ? (
                                      <CheckIcon />
                                    ) : (
                                      <ScheduleIcon />
                                    )
                                  }
                                  label={
                                    video.processed ? "Processed" : "Review"
                                  }
                                  size="small"
                                  color={
                                    video.processed ? "success" : "warning"
                                  }
                                  sx={{
                                    bgcolor: video.processed
                                      ? "rgba(76, 175, 80, 0.9)"
                                      : "rgba(255, 152, 0, 0.9)",
                                    color: "white",
                                  }}
                                />

                                {/* Video type indicator - fixed positioning */}
                                <TypeChip
                                  icon={getVideoTypeIcon(video)}
                                  label={getVideoTypeLabel(video)}
                                  size="small"
                                  color={
                                    video.metadata?.exercise
                                      ? "primary"
                                      : "secondary"
                                  }
                                  sx={{
                                    bgcolor: video.metadata?.exercise
                                      ? "rgba(25, 118, 210, 0.9)"
                                      : "rgba(156, 39, 176, 0.9)",
                                    color: "white",
                                  }}
                                />
                              </ThumbnailContainer>

                              <CardContent sx={{ p: 2 }}>
                                {/* Show exercise name if it exists
                                {video.metadata?.exercise && (
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{ mb: 1 }}
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      fontWeight="medium"
                                      color="primary"
                                      sx={{
                                        maxWidth: "100%",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {video.metadata.notes ||
                                        "Unknown Exercise"}
                                    </Typography>
                                  </Stack>
                                )} */}

                                {/* Video notes (truncated) */}
                                {video.metadata?.notes && (
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{ mb: 1 }}
                                  >
                                    <DescriptionIcon
                                      fontSize="small"
                                      color="action"
                                    />
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{
                                        maxWidth: "100%",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {video.metadata?.notes}
                                    </Typography>
                                  </Stack>
                                )}

                                <Stack spacing={1}>
                                  {/* Date information */}
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                  >
                                    <ScheduleIcon
                                      fontSize="small"
                                      color="disabled"
                                    />
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      noWrap
                                    >
                                      {formatDate(video.createdOn)}
                                    </Typography>
                                  </Stack>

                                  {/* Processed information */}
                                  {video.processed && video.processedBy && (
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      spacing={1}
                                    >
                                      <PersonIcon
                                        fontSize="small"
                                        color="disabled"
                                      />
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        noWrap
                                      >
                                        Processed by{" "}
                                        {video.processedBy.firstName}{" "}
                                        {video.processedBy.lastName}
                                      </Typography>
                                    </Stack>
                                  )}

                                  {/* Upload timeframe */}
                                  <Stack
                                    direction="row"
                                    justifyContent="flex-end"
                                  >
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {moment(
                                        video.createdOn.toDate(),
                                      ).fromNow()}
                                    </Typography>
                                  </Stack>
                                </Stack>
                              </CardContent>
                            </VideoThumbnail>
                          </VideoCard>
                        </Grid>
                  ))}
                </Grid>
              ) : (
                <Paper
                  sx={{
                    p: 3,
                    textAlign: "center",
                    bgcolor: "grey.50",
                    borderStyle: "dashed",
                    borderWidth: 2,
                    borderColor: "grey.300",
                  }}
                >
                  <VideoIcon sx={{ fontSize: 48, color: "grey.400", mb: 2 }} />
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    No videos shared by this client yet
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 2, display: "block" }}
                  >
                    When the client shares videos, they will appear here for
                    your review
                  </Typography>
                </Paper>
              )}
            </VideosContent>
          </StyledVideosCard>
        </Grid>
      </Grid>

      {/* Video Viewer Dialog */}
      <Dialog
        open={openViewer}
        onClose={() => setOpenViewer(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedVideo && (
          <>
            <DialogContent sx={{ p: 2, pb: 1 }}>
              <Stack spacing={1}>
                {/* Video player */}
                <Box
                  sx={{
                    textAlign: "center",
                    bgcolor: "black",
                    borderRadius: 1,
                    position: "relative",
                    height: "auto",
                    maxHeight: "50vh",
                  }}
                >
                  <video
                    controls
                    autoPlay
                    poster={thumbnails[selectedVideo.id]}
                    style={{
                      width: "100%",
                      maxHeight: "50vh",
                      objectFit: "contain",
                    }}
                  >
                    <source src={selectedVideo.url} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </Box>

                {/* Title and action buttons */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mt: 1 }}
                >
                  <Typography variant="h6" fontWeight="medium">
                    {selectedVideo.metadata?.exercise &&
                    selectedVideo.metadata.exercise.id
                      ? getExerciseName(selectedVideo.metadata.exercise.id)
                      : "General Shared Video"}
                  </Typography>

                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant={
                        selectedVideo.processed ? "outlined" : "contained"
                      }
                      color={selectedVideo.processed ? "warning" : "success"}
                      startIcon={
                        selectedVideo.processed ? <UndoIcon /> : <CheckIcon />
                      }
                      onClick={() => handleToggleProcessed(selectedVideo)}
                    >
                      {selectedVideo.processed ? "Unprocess" : "Mark Processed"}
                    </Button>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteVideo(selectedVideo)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </Stack>

                {/* Status information */}
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ mb: 0.5 }}
                >
                  <Chip
                    icon={getVideoTypeIcon(selectedVideo)}
                    label={getVideoTypeLabel(selectedVideo)}
                    color={
                      selectedVideo.metadata?.exercise ? "primary" : "secondary"
                    }
                    size="small"
                  />
                  <Typography variant="body2" color="text.secondary">
                    Shared {moment(selectedVideo.createdOn.toDate()).fromNow()}
                  </Typography>
                  {selectedVideo.processed && selectedVideo.processedBy && (
                    <Typography variant="body2" color="success.main">
                      Processed by {selectedVideo.processedBy.firstName}
                    </Typography>
                  )}
                </Stack>

                {/* Exercise details if available - more compact */}
                {selectedVideo.metadata?.exercise &&
                  selectedVideo.metadata.exercise.id && (
                    <Paper
                      sx={{ p: 1, bgcolor: "primary.light + '10'" }}
                      variant="outlined"
                    >
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <FitnessCenterIcon
                              fontSize="small"
                              color="primary"
                            />
                            <Typography variant="body2" noWrap>
                              {getExerciseName(
                                selectedVideo.metadata.exercise.id,
                              )}
                            </Typography>
                          </Stack>
                        </Grid>
                        <Grid item xs={3}>
                          {selectedVideo.metadata.exercise.sets && (
                            <Typography variant="body2">
                              Sets: {selectedVideo.metadata.exercise.sets}
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={3}>
                          {selectedVideo.metadata.exercise.reps && (
                            <Typography variant="body2">
                              Reps: {selectedVideo.metadata.exercise.reps}
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Paper>
                  )}

                {/* Coach notes - more compact */}
                <Box sx={{ mt: 0 }}>
                  <TextField
                    multiline
                    rows={3}
                    fullWidth
                    size="small"
                    placeholder="Add your notes about this video..."
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mr: 1, whiteSpace: "nowrap" }}
                        >
                          Coach Notes:
                        </Typography>
                      ),
                    }}
                  />
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 2, pt: 0, pb: 1 }}>
              <Button size="small" onClick={() => setOpenViewer(false)}>
                Close
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveNote}
              >
                Save Notes
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Upload Dialog - We keep this for reference, but in reality clients would upload from their app */}
      <Dialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <VideosHeader>
          <Typography variant="h6" fontWeight="bold">
            Upload Video
          </Typography>
        </VideosHeader>
        <DialogContent>
          <UploadZone>
            <Stack spacing={2} alignItems="center">
              <VideoIcon sx={{ fontSize: 48, color: "primary.main" }} />
              <Typography variant="body1">
                Click or drag video files here
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Supported formats: MP4, MOV, AVI (Max 100MB)
              </Typography>
            </Stack>
          </UploadZone>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>Cancel</Button>
          <Button variant="contained">Upload</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EnhancedVideosTab;
